import { Location } from "@angular/common";
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AgCartesianSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { ManufacturerReceiptTime } from "app/model/entities/-chart-classes";

import { DateFns } from "app/shared/date-fns";
import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const MONTHS_BACK = 6;
const TOP_MANUFACTURER_COUNT = 10;

/** One pair of bars: a manufacturer's actual receipt time against what it promised. */
interface ManufacturerBar {
  manufacturerName: string;
  actualDays: number;
  promisedDays: number;
  unitCount: number;
  poCount: number;
  openUnitCount: number;
}

/** A detail row — one purchase order, with its own average derived from UnitCount + TotalDays. */
interface ReceiptRow extends ManufacturerReceiptTime {
  actualDays: number;
  daysLate: number;
}

@Component({
  selector: "app-chart-manufacturer-receipt-time",
  templateUrl: "./chart-manufacturer-receipt-time.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartManufacturerReceiptTimeComponent extends DomainBaseComponent implements OnInit, OnChanges {
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];
  options: any;
  private detailRows: ReceiptRow[] = [];
  private barData: ManufacturerBar[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.processChartData();
    this.isPageReady = true;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes["includedAccountIds"] && !changes["includedAccountIds"].firstChange) {
      await this.processChartData();
    }
  }

  private async processChartData() {
    const data = (await this.dbQueryService.getManufacturerReceiptTimes(MONTHS_BACK, this.includedAccountIds)) ?? [];
    DateFns.fixupDatesInProjection(data, ["poDate"]);

    this.detailRows = _.orderBy(
      data.map((d) => {
        const actualDays = this.avg(d.totalDays, d.unitCount);
        return { ...d, actualDays, daysLate: _.round(actualDays - d.expectedDays, 1) };
      }),
      ["manufacturerName", "poDate"]
    );

    this.barData = this.buildBars(data);

    if (this.barData.length === 0) {
      this.options = null;
      return;
    }

    this.options = {
      title: { text: this.getTitle() },
      data: this.barData,
      series: [
        {
          type: "bar",
          xKey: "manufacturerName",
          yKey: "actualDays",
          yName: "Actual days",
          tooltip: { renderer: this.renderer.bind(this) },
        },
        {
          type: "bar",
          xKey: "manufacturerName",
          yKey: "promisedDays",
          yName: "Promised days",
          tooltip: { renderer: this.renderer.bind(this) },
        },
      ],
      axes: [
        { type: "category", position: "bottom", label: { rotation: -45 } },
        { type: "number", position: "left", title: { text: "Days" } },
      ],
    };
  }

  /** Top manufacturers by received volume, slowest first.
   *  Both averages are weighted by units — summed days over summed units, never averaged averages. */
  private buildBars(data: ManufacturerReceiptTime[]): ManufacturerBar[] {
    const byManufacturer = _.map(_.groupBy(data, (d) => d.manufacturerId), (rows) => {
      const unitCount = _.sumBy(rows, (r) => r.unitCount);
      return {
        manufacturerName: rows[0].manufacturerName,
        actualDays: this.avg(_.sumBy(rows, (r) => r.totalDays), unitCount),
        promisedDays: this.avg(_.sumBy(rows, (r) => r.expectedDays * r.unitCount), unitCount),
        unitCount,
        poCount: rows.length,
        openUnitCount: _.sumBy(rows, (r) => r.openUnitCount),
      } as ManufacturerBar;
    });

    const topByVolume = _.orderBy(byManufacturer, (m) => m.unitCount, "desc").slice(0, TOP_MANUFACTURER_COUNT);
    return _.orderBy(topByVolume, (m) => m.actualDays, "desc");
  }

  private avg(total: number, count: number) {
    return count === 0 ? 0 : _.round(total / count, 1);
  }

  getTitle() {
    return `Average Receipt Time by Manufacturer - Last ${MONTHS_BACK} Months`;
  }

  renderer(params: AgCartesianSeriesTooltipRendererParams) {
    const d = params.datum as ManufacturerBar;
    const openText = d.openUnitCount > 0 ? `<br/>${d.openUnitCount} units still on order` : "";
    return {
      title: d.manufacturerName,
      content:
        `${d.actualDays} days actual vs ${d.promisedDays} promised` +
        `<br/>${d.unitCount} units received on ${d.poCount} purchase orders` +
        openText,
    };
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Receipt Time");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:G1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:G2");

    worksheet.addRow([]);

    worksheet.addRow(["Manufacturer", "P/O", "P/O Date", "Units Received", "Still On Order", "Actual Days", "Promised Days"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 32;
    worksheet.getColumn(2).width = 12;
    worksheet.getColumn(3).width = 14;
    worksheet.getColumn(4).width = 16;
    worksheet.getColumn(5).width = 16;
    worksheet.getColumn(6).width = 14;
    worksheet.getColumn(7).width = 16;

    this.detailRows.forEach((r) => {
      worksheet.addRow([
        r.manufacturerName,
        r.poHeaderId,
        r.poDate,
        r.unitCount,
        r.openUnitCount,
        r.actualDays,
        r.expectedDays,
      ]);
    });
    worksheet.getColumn(3).numFmt = "mm/dd/yyyy";

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Manufacturer_Receipt_Time_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): ReceiptRow[] {
    return this.detailRows;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "manufacturerName", headerName: "Manufacturer", flex: 2 },
      { field: "poHeaderId", headerName: "P/O", flex: 1, type: "leftAligned" },
      { field: "poDate", headerName: "P/O Date", flex: 1 },
      { field: "unitCount", headerName: "Units Received", flex: 1, type: "leftAligned" },
      { field: "openUnitCount", headerName: "Still On Order", flex: 1, type: "leftAligned" },
      { field: "actualDays", headerName: "Actual Days", flex: 1, type: "leftAligned" },
      { field: "expectedDays", headerName: "Promised Days", flex: 1, type: "leftAligned" },
      { field: "daysLate", headerName: "Days Late", flex: 1, type: "leftAligned" },
    ];
  }

  getExportFileName(): string {
    return "Manufacturer_Receipt_Time";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
